import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useAsset } from '../../../asset/asset-context';
import { useNetwork } from '../../../network/network-context';
import { IbcTransferContextProvider } from '../../ibc-transfer-context';
import { DepositWithdrawMode } from '../../ibc-transfer-types';
import { BridgeTransfer } from '../history/bridge-hisotry-types';
import { BridgeHistoryContextProvider } from '../history/bridge-history-context';

interface BridgeDepositWithdrawContextValue {
    depositWithdrawMode?: DepositWithdrawMode;
    setDepositWithdrawMode: (value?: DepositWithdrawMode) => void;
    setEnabled: (value: boolean) => void;
    setSourceId: (value: string) => void;
    setDestinationId: (value: string) => void;
    setOnExploreClick: (callback: (transfer: BridgeTransfer) => void) => void;
}

export const BridgeDepositWithdrawContext = createContext<BridgeDepositWithdrawContextValue>({} as BridgeDepositWithdrawContextValue);

export const useBridgeDepositWithdraw = (): BridgeDepositWithdrawContextValue => useContext(BridgeDepositWithdrawContext);

export const BridgeDepositWithdrawContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { hubNetwork, getNetwork } = useNetwork();
    const { vsAsset } = useAsset();
    const [ depositWithdrawMode, setDepositWithdrawMode ] = useState<DepositWithdrawMode>();
    const [ sourceId, setSourceId ] = useState<string>();
    const [ destinationId, setDestinationId ] = useState<string>();
    const [ enabled, setEnabled ] = useState(false);
    const [ onExploreClick, setOnExploreClick ] = useState<(transfer: BridgeTransfer) => void>();

    const optionalNetworks = useMemo(() => (process.env.REACT_APP_DEPOSIT_WITHDRAW_NETWORKS?.split(',') || [])
        .filter((networkId) => getNetwork(networkId)), [ getNetwork ]);

    const sourceNetworks = useMemo(
        () => !depositWithdrawMode ? [] : depositWithdrawMode === 'Deposit' ? optionalNetworks : hubNetwork ? [ hubNetwork.chainId ] : [],
        [ depositWithdrawMode, hubNetwork, optionalNetworks ],
    );

    const destinationNetworks = useMemo(
        () => !depositWithdrawMode ? [] : depositWithdrawMode !== 'Deposit' ? optionalNetworks : hubNetwork ? [ hubNetwork.chainId ] : [],
        [ depositWithdrawMode, hubNetwork, optionalNetworks ],
    )

    return (
        <BridgeDepositWithdrawContext.Provider
            value={{ depositWithdrawMode, setDepositWithdrawMode, setEnabled, setSourceId, setDestinationId, setOnExploreClick }}
        >
            <BridgeHistoryContextProvider>
                <IbcTransferContextProvider
                    optionalSourceNetworks={sourceNetworks}
                    optionalDestinationNetworks={destinationNetworks}
                    initialAsset={vsAsset}
                    enable={enabled}
                    initialSourceId={sourceId}
                    initialDestinationId={destinationId}
                    allowBridging
                    onExploreClick={onExploreClick}
                >
                    {children}
                </IbcTransferContextProvider>
            </BridgeHistoryContextProvider>
        </BridgeDepositWithdrawContext.Provider>
    );
};
